/**
 * Canonical answer-map shape for equality checks.
 *
 * Belongs here: drop empties, sort keys / multi-select ids.
 * Does not belong here: submit payload shaping or flow rules.
 */
import isEqual from 'lodash/isEqual'
import type { AllocationAnswersMap, QuestionAnswerValue } from '../shared/types/questionnaire.types'

function normalizeAnswerValue(value: QuestionAnswerValue): QuestionAnswerValue | undefined {
  if (typeof value === 'string') {
    return value === '' ? undefined : value
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  // Corrupt / unexpected shapes must not throw on .length
  if (!Array.isArray(value)) return undefined
  // Multi-select: order of option ids must not count as a change
  if (value.length === 0) return undefined
  return [...value].sort()
}

/** Stable copy of an answers map for comparison (ignores empties and option-id order). */
export function normalizeAnswersMap(answers: AllocationAnswersMap): AllocationAnswersMap {
  const normalized: AllocationAnswersMap = {}
  for (const questionId of Object.keys(answers ?? {}).sort()) {
    const value = answers[questionId]
    if (value === undefined || value === null) continue
    const nextValue = normalizeAnswerValue(value)
    if (nextValue === undefined) continue
    normalized[questionId] = nextValue
  }
  return normalized
}

/** True when two answer maps are the same after normalization. */
export function areAnswersEqual(
  left: AllocationAnswersMap,
  right: AllocationAnswersMap
): boolean {
  return isEqual(normalizeAnswersMap(left), normalizeAnswersMap(right))
}
